import isEqual from 'lodash-es/isEqual';
import type { Store, Unsubscribe } from '@reduxjs/toolkit';

import { cadenzaSessionStorage } from 'cadenza/utils/session-storage/session-storage';

/**
 * Calls a callback function whenever there is a state change.
 *
 * @param store - The store to observe
 * @param select - Select a portion of the state.
 * @param onChange - Callback function that will be called with the new state
 * @param [compareStateByValue=false] - If true, the result of the selector is compared with the previous value by nested value and not just equality, using the lodash isEqual() function. Use wisely, as it has potential performance impact.
 * @return - Unsubscribe function. When this function is called (without any params), the
 *   store removes the corresponding callback from its array of listeners.
 */
export function observeStore<S, O> (
  store: Store<S>,
  select: (state: S) => O,
  onChange: (nextState: O, currentState?: O) => void,
  compareStateByValue = false
) {
  let currentState: O | undefined; // old state

  function handleChange () {
    const nextState = select(store.getState());
    if (hasStateChanged(currentState, nextState, compareStateByValue)) {
      onChange(nextState, currentState);
      currentState = nextState;
    }
  }

  const unsubscribe = store.subscribe(handleChange);
  handleChange();
  return unsubscribe;
}

function hasStateChanged<S> (oldState: S | undefined, newState: S, compareStateByValue: boolean) {
  if (compareStateByValue) {
    return !isEqual(oldState, newState);
  } else {
    return oldState !== newState;
  }
}

/**
 * Removes store observer(s) using the function returned by {@link observeStore}.
 *
 * @param unsubscribe - redux unsubscribe functions
 */
export function unsubscribeStore (unsubscribe: undefined | Unsubscribe | Unsubscribe[]) {
  if (unsubscribe) {
    const unsubscribes = Array.isArray(unsubscribe) ? unsubscribe : [ unsubscribe ];
    unsubscribes.forEach(unsubscribe => unsubscribe());
  }
}

/**
 * Enum for the status of an async operation.
 *
 * @enum {string}
 * @readonly
 */
export const STATUS = {
  PENDING: 'pending',
  SUCCEEDED: 'succeeded',
  FAILED: 'failed'
} as const;

export type STATUS = typeof STATUS[keyof typeof STATUS];

/**
 * Gets an item from sessionStorage as a boolean value.
 *
 * @param item - The name of the item to get
 * @param [defaultValue=false] - The default value to return if the item is not set
 * @return The boolean value
 */
export function booleanFromSessionStorage (item: string, defaultValue = false) {
  const value = cadenzaSessionStorage.getItem(item);
  return value ? value === 'true' : defaultValue;
}
